import { ENV } from "../Utils";

export class QuizApi {
  baseApi = ENV.BASE_API;

  buildStudentPayload(quiz, answers) {
    const teacherQuizId = quiz?._id || quiz?.id || quiz?.teacherQuizId;
    const questionsLen = Array.isArray(quiz?.questions)
      ? quiz.questions.length
      : 0;

    const responses = (answers || []).map((a) => ({
      questionIndex: Number(a?.questionIndex) || 0,
      selected: typeof a?.selected === "string" ? a.selected : "",
      // dejamos timeTaken por respuesta para que el backend pueda sumarlo si falta el total
      timeTaken: Number(a?.timeTaken) || 0,
    }));

    const timeTaken = Math.round(
      responses.reduce((acc, r) => acc + (Number(r.timeTaken) || 0), 0),
    );

    const totalTime = questionsLen * 20; // cada pregunta 20s en el modal

    return {
      teacherQuizId,
      responses,
      timeTaken,
      totalTime,
    };
  }

  /**
   * Atajo: recibe quiz + answers del modal y registra el intento.
   * Mantiene intacto submitStudentQuiz(data) para compatibilidad.
   */
  async submitStudentQuizFromAnswers(accessToken, studentId, quiz, answers) {
    const payload = this.buildStudentPayload(quiz, answers);
    return this.submitStudentQuiz(accessToken, studentId, payload);
  }

  // GET: Obtener todos los quizz asociados a un teacher (se pasa teacherId en la URL)
  async getAllQuizzes(accessToken, teacherId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.QUIZZES}/getall/${teacherId}`;
      const params = {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  async setActiveTeacherQuiz(accessToken, teacherId, quizId) {
    const url = `${this.baseApi}/${ENV.API_ROUTES.QUIZZES}/activeTeacher/${teacherId}`;
    const params = {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(quizId ? { quizId } : {}),
    };
    const response = await fetch(url, params);
    const result = await response.json();
    if (response.status !== 200) throw result;
    return result;
  }
  // GET: Obtener un quizz por su ID
  async getQuizById(accessToken, quizId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.QUIZZES}/get/${quizId}`;
      const params = {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  // POST: Crear un nuevo quizz (requiere teacherId en la URL)
  async createQuiz(accessToken, teacherId, data) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.QUIZZES}/create/${teacherId}`;
      const params = {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 201) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  // PATCH: Actualizar un quizz existente
  async updateQuiz(accessToken, quizId, data) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.QUIZZES}/update/${quizId}`;
      const params = {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  // DELETE: Eliminar un quizz
  async deleteQuiz(accessToken, quizId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.QUIZZES}/delete/${quizId}`;
      const params = {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  // PATCH: Activar un quizz en una ruta didáctica (asocia el quizz a la ruta)
  async activateQuiz(accessToken, routeId, quizId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.QUIZZES}/active/${routeId}/${quizId}`;
      const params = {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  // GET: Obtener los resultados (intentos de alumnos) de un quizz
  async getQuizResults(accessToken, quizId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.QUIZZES}/results/${quizId}`;
      const params = {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  // GET: Obtener ranking de un quizz para un teacher y classnumber
  async getRankingResults(accessToken, teacherId, quizId, classnumber) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.QUIZZES}/ranking/${teacherId}/${quizId}/${classnumber}`;
      const params = {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  // POST: Registrar el intento de quizz realizado por un alumno
  async submitStudentQuiz(accessToken, studentId, data) {
    try {
      // data debe incluir: teacherQuizId, responses (array), timeTaken, y totalTime (opcional)
      const url = `${this.baseApi}/${ENV.API_ROUTES.QUIZZES}/student/${studentId}`;
      const params = {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  // GET: Obtener resultado detallado del quizz para un alumno
  async getDetailedStudentResult(accessToken, studentId, quizId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.QUIZZES}/studentresult/${studentId}/${quizId}`;
      const params = {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  // F6: GET examen para REALIZAR (alumno) SIN la respuesta correcta.
  async getQuizForPlay(accessToken, quizId) {
    const url = `${this.baseApi}/${ENV.API_ROUTES.QUIZZES}/play/${quizId}`;
    const response = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const result = await response.json();
    if (response.status !== 200) throw result;
    return result; // { code, quiz: { _id, name, questions:[{question, responses[]}], totalQuestions } }
  }

  // F6: GET historial de intentos del alumno para un examen (+ mejor marca).
  async getStudentQuizAttempts(accessToken, studentId, quizId) {
    const url = `${this.baseApi}/${ENV.API_ROUTES.QUIZZES}/attempts/${studentId}/${quizId}`;
    const response = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const result = await response.json();
    if (response.status !== 200) throw result;
    return result; // { code, attempts:[...], best, attemptsCount }
  }

  // Dashboard profesor (E1): GET resumen de una clase (alumnos + media % + media clase).
  async getClassSummary(accessToken, teacherId, classnumber) {
    const url = `${this.baseApi}/${ENV.API_ROUTES.QUIZZES}/classsummary/${teacherId}/${encodeURIComponent(
      classnumber,
    )}`;
    const response = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const result = await response.json();
    if (response.status !== 200) throw result;
    return result; // { code, data: { classnumber, examsTotal, students:[...], classAverage } }
  }

  // Dashboard profesor (E2): GET historial de examenes de un alumno (mejor marca %).
  async getStudentExams(accessToken, studentId, teacherId) {
    const url = `${this.baseApi}/${ENV.API_ROUTES.QUIZZES}/studentexams/${studentId}/${teacherId}`;
    const response = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const result = await response.json();
    if (response.status !== 200) throw result;
    return result; // { code, data: { student, exams:[...] } }
  }
}
